<template>
  <div>
    

    <!-- Resumen -->
    <v-card class="mb-3 pa-4" outlined rounded="lg">
      <div class="d-flex align-center justify-space-between mb-3">
      <div class="orbitron-title">Plantilla</div>

      <v-btn small outlined @click="$emit('refresh', activeLigaId)" class="actualizar">
        Actualizar
      </v-btn>
    </div>
      <v-card-text>
        <div class="d-flex align-center justify-space-between">
          <div>
            <div class="text-caption grey--text">Puntos Totales</div>
            <div class="text-h6">{{ totalPoints }}</div>
          </div>
          <div class="text-right">
            <div class="text-caption grey--text">Presupuesto</div>
            <div class="text-h6">{{ money(budget) }}</div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Activos -->
    <v-card class="mb-3" outlined rounded="lg">
      <v-card-text>
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="subtitulo-2 font-weight-medium">Visualización de Activos</div>          
          <div class="text-right">
            <div class="text-caption grey--text text--darken-1">
              Capacidad: <span :class="totalUsedCells >= MAX_GRID_CELLS ? 'red--text font-weight-bold' : ''">{{ totalUsedCells }} / {{ MAX_GRID_CELLS }}</span> celdas
            </div>          
            <div class="text-caption grey--text text--darken-1">
              Activos operativos: {{ operativeAssetsCount }} / {{ ownedGeneratorsCount }}
            </div>
          </div>
        </div>
        <v-alert
        v-if="totalUsedCells > MAX_GRID_CELLS"
        type="warning"
        outlined
        dense
        class="mb-3"
      >
        <strong>Plantilla por encima del límite.</strong>

        Estás utilizando
        <strong>{{ totalUsedCells }} / {{ MAX_GRID_CELLS }} casillas</strong>.

        Desactiva algún activo para volver a estar dentro de la capacidad permitida.
        Si mantienes la plantilla por encima del límite, podría afectar a la
        puntuación de tus activos.
      </v-alert>
        <div class="company-map-wrapper">
          <MyMap :unidades="ownedGenerators" />
        </div>

        <div class="factory-board mb-4">
  <div class="factory-roof">
    <div class="factory-title">
      <span class="material-symbols-outlined mr-1">factory</span>
      Centro operativo
    </div>

    <div class="factory-chimneys">
      <span></span>
      <span></span>
      <span></span>
    </div>
  </div>

  <div class="factory-status">
    <div>
      <div class="factory-label">Capacidad ocupada</div>
      <div class="factory-value">
        {{ totalUsedCells }} / {{ MAX_GRID_CELLS }} celdas
      </div>
    </div>

    <div class="factory-energy-bar">
      <div
        class="factory-energy-fill"
        :class="{ 'factory-energy-full': totalUsedCells >= MAX_GRID_CELLS }"
        :style="{ width: capacityPercent + '%' }"
      ></div>
    </div>
  </div>

  <div class="factory-grid">
    <div 
      v-for="(cell, i) in gridSquares" 
      :key="i"
      class="factory-cell"
      :class="cell ? techClass(cell.type) : 'empty'"
    >
      <span v-if="cell" class="material-symbols-outlined factory-cell-icon">
        {{ getIcon(cell.type) }}
      </span>

      <span v-else class="factory-empty-dot"></span>

      <div v-if="cell" class="factory-cell-tooltip">
        <b>{{ cell.name }}</b><br>
        {{ cell.type }}
      </div>
    </div>
  </div>

  <div class="factory-floor">
    <div class="factory-floor-line"></div>
    <div class="factory-floor-label">
      Solo los activos activados ocupan módulos de la planta.
    </div>
  </div>
</div>

        <v-divider class="my-3"></v-divider>

        <div class="text-subtitle-2 font-weight-medium mb-2">Listado de Activos</div>

        <v-row dense class="mb-2">
          <v-col cols="4">
            <div class="text-caption grey--text">Generadores</div>
            <div class="text-subtitle-1 font-weight-medium">
              {{ ownedGeneratorsCount }}
            </div>
          </v-col>

          <v-col cols="4">
            <div class="text-caption grey--text">Capacidad total</div>
            <div class="text-subtitle-1 font-weight-medium">
              {{ totalCapacity }} MW
            </div>
          </v-col>

          <v-col cols="4" class="text-right">
            <div class="text-caption grey--text">Capacidad operativa</div>
            <div class="text-subtitle-1 font-weight-medium primary--text">
              {{ totalOperativeCapacity }} MW
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-alert v-if="ownedGenerators.length === 0" color="primary" dense>
          Aún no has comprado activos.
        </v-alert>

        <v-list v-else dense>
          <v-list-item
            v-for="g in ownedGenerators"
            :key="g.id"
            class="asset-list-item"
          >
            <v-list-item-content>
             <v-list-item-title class="font-weight-medium generator-title">
            <span class="generator-name">
              {{ g.name }} --- {{ g.type }}
            </span>

            <button
              type="button"
              class="history-info-btn"
              title="Ver histórico de ganancias"
              @click.stop="openAssetHistory(g)"
            >
              <span class="material-symbols-outlined history-info-icon">
                info
              </span>
            </button>

            <span v-if="g.type == 'Eólica'" class="material-symbols-outlined tech-icon">
              mode_fan
            </span>

            <span v-if="g.type == 'Fotovoltaica'" class="material-symbols-outlined tech-icon">
              solar_power
            </span>

            <span v-if="g.type == 'Hidráulica'" class="material-symbols-outlined tech-icon">
              water
            </span>

            <span v-if="g.type == 'Nuclear'" class="material-symbols-outlined tech-icon">
              orbit
            </span>

            <span v-if="g.type == 'Ciclo combinado'" class="material-symbols-outlined tech-icon">
              factory
            </span>

            <span v-if="g.type == 'Carbón'" class="material-symbols-outlined tech-icon">
              local_fire_department
            </span>

            <span class="tech-cells-badge">
              {{ getTechCells(g.type) }} celda{{ getTechCells(g.type) === 1 ? '' : 's' }}
            </span>
          </v-list-item-title>
              <v-list-item-subtitle>
                Tipo: {{ g.type }} · Capacidad: {{ g.capacity }} MW · Precio compra:
                {{ money(g.purchasePrice || 0) }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action class="asset-actions">
              <v-btn
                small
                :color="g.operativo ? 'warning' : 'success'"
                @click="toggleOp(g)"
                :disabled="!g.operativo && !canActivate(g)"
              >
                {{ g.operativo ? "Desactivar" : "Activar" }}
              </v-btn>

              <v-btn
                v-if="isSolar(g)"
                small
                :color="hasExpiredBatteries(g) ? 'error' : 'secondary'"
                :outlined="!hasExpiredBatteries(g)"
                :class="[
                  'asset-battery-btn',
                  { 'asset-battery-btn-expired': hasExpiredBatteries(g) }
                ]"
                @click="openBatteryModal(g)"
              >
                Baterías {{ batteryCount(g) }}/2
              </v-btn>
              <v-btn
                small
                class="asset-sell-market-btn"
                :disabled="!!g.venta_bloqueada_id"
                @click="emitSellMarket(g)"
              >
                {{ g.venta_bloqueada_id ? "Ya en venta" : "Poner en venta" }}
              </v-btn>

              <v-btn
                small
                color="error"
                outlined
                :disabled="!!g.venta_bloqueada_id"
                @click="confirmSellInstant(g)"
              >
                {{ g.venta_bloqueada_id ? "Ya en venta" : "Vender al instante" }}
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
    <!-- Modal histórico del activo -->
    <v-dialog
      v-model="historyDialog"
      max-width="560px"
      :z-index="3000"
    >
      <v-card rounded="lg">
        <v-card-title class="headline grey lighten-4">
          <div>
            <div class="modal-history-title">
              Histórico
            </div>

            <div
              v-if="selectedAssetHistory"
              class="text-caption grey--text"
            >
              {{ selectedAssetHistory.name }}
            </div>
          </div>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-progress-linear
            v-if="loadingHistory"
            indeterminate
            color="primary"
            class="mb-4"
          />

         <div
  v-if="historyClausulazoPrice !== null"
  class="history-clausulazo-box mb-3"
>
  <div class="history-clausulazo-main">
    <div class="history-clausulazo-label">
      <span class="material-symbols-outlined">
        shield
      </span>

      <span>
        Valor de clausulazo
      </span>
    </div>

    <div class="history-clausulazo-price">
      {{ money(historyClausulazoPrice) }}
    </div>
  </div>

  <div
    v-if="
      selectedAssetHistory &&
      selectedAssetHistory.clausulazo_bloqueado
    "
    class="history-clausulazo-protection"
  >
    <span class="material-symbols-outlined">
      verified_user
    </span>

    <span>
      Protegido hasta
      <strong>
        {{
          formatFechaProteccion(
            selectedAssetHistory.clausulazo_bloqueado_hasta
          )
        }}
      </strong>
    </span>
  </div>

  <div
    v-else-if="selectedAssetHistory"
    class="history-clausulazo-unprotected"
  >
    <span class="material-symbols-outlined">
      shield_lock
    </span>

    <span>
      Sin protección de clausulazo
    </span>
  </div>


          

          

          </div>

          <div
            v-if="!loadingHistory && assetHistory.length === 0"
            class="text-center py-4 grey--text"
          >
            Este activo todavía no tiene histórico de puntos.
          </div>

          <div v-if="!loadingHistory && assetHistory.length > 0">
            <div class="text-caption grey--text mb-3">
              Últimas 5 puntuaciones registradas para este activo.
            </div>

            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th class="text-right">Ganancia</th>
                    <th class="text-right">Batería</th>
                    <th class="text-right">Puntos</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(h, index) in assetHistory"
                    :key="`${h.fecha || index}-${index}`"
                  >
                    <td>
                      {{ formatDate(h.fecha) }}
                    </td>

                    <td class="text-right">
                      {{ money(h.ganancia_activo) }}
                    </td>

                    <td class="text-right">
                    <span
                      v-if="Number(h.puntos_bateria || 0) !== 0"
                      class="battery-history-points"
                    >
                      {{ formatPoints(h.puntos_bateria) }} pts
                    </span>

                    <span v-else class="grey--text">
                      —
                    </span>
                  </td>

                    <td class="text-right">
                      <span
                        class="asset-history-points"
                        :class="
                          Number(h.puntos_activo || 0) >= 0
                            ? 'green--text'
                            : 'red--text'
                        "
                      >
                        {{ formatPoints(h.puntos_activo) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            text
            @click="closeAssetHistory"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Fin modal histórico -->
    <v-dialog v-model="batteryDialog" max-width="650px">
  <v-card v-if="selectedSolar" rounded="lg">
    <v-card-title class="headline grey lighten-4 d-flex align-center">
  <span class="material-symbols-outlined mr-2 battery-title-icon">
    battery_charging_full
  </span>

  <span class="battery-modal-title">
    Baterías de {{ selectedSolar.name }}
  </span>
</v-card-title>

    <v-card-text>
      <v-alert dense outlined color="secondary" class="battery-info-alert">
        Cada batería permite almacenar energía equivalente al
        <b>50% de la potencia solar</b> durante <b>2 horas</b>.
        Dura <b>14 días</b> y se degrada un <b>2% diario</b>.
      </v-alert>

      <div class="battery-summary-box mb-3">
        <div>
          <div class="text-caption grey--text">Potencia solar</div>
          <div class="font-weight-medium">{{ selectedSolar.capacity }} MW</div>
        </div>

        <div>
          <div class="text-caption grey--text">Potencia por batería</div>
          <div class="font-weight-medium">
            {{ batteryPower(selectedSolar) }} MW
          </div>
        </div>

        <div>
          <div class="text-caption grey--text">Energía por batería</div>
          <div class="font-weight-medium">
            {{ batteryEnergy(selectedSolar) }} MWh
          </div>
        </div>
      </div>

      <div class="text-subtitle-2 font-weight-medium mb-2">
        Baterías instaladas
      </div>

      <v-alert
        v-if="batteryCount(selectedSolar) === 0"
        dense
        outlined
        color="info"
      >
        Esta planta todavía no tiene baterías instaladas.
      </v-alert>

      <!-- Vista móvil -->
<div
  v-else-if="$vuetify.breakpoint.smAndDown"
  class="battery-mobile-list"
>
  <div
    v-for="b in getBatteries(selectedSolar)"
    :key="b.id"
    class="battery-mobile-card"
    :class="{ 'battery-mobile-card-expired': isBatteryExpired(b) }"
  >
    <div class="battery-mobile-top">
      <div class="battery-mobile-slot">
        Slot {{ b.slot_num || b.slot || "-" }}
      </div>

      <v-chip
        x-small
        :color="isBatteryExpired(b) ? 'error' : 'success'"
        dark
        :class="{ 'battery-expired-chip': isBatteryExpired(b) }"
      >
        {{ isBatteryExpired(b) ? "Caducada" : "Activa" }}
      </v-chip>
    </div>

    <div class="battery-mobile-info">
      <div class="battery-mobile-item">
        <div class="battery-mobile-label">Eficiencia</div>
        <div class="battery-mobile-value">
          {{ batteryEfficiencyText(b) }}
        </div>
      </div>

      <div class="battery-mobile-item">
        <div class="battery-mobile-label">Fin</div>
        <div class="battery-mobile-value">
          {{ formatDate(b.fecha_fin || b.end_date) }}
        </div>
      </div>
    </div>

    <v-btn
      v-if="canRevampBattery(b)"
      block
      small
      color="error"
      outlined
      :loading="batteryLoading"
      @click="confirmRevampBattery(b)"
    >
      Revamping
    </v-btn>

    <div
      v-else
      class="text-caption grey--text text-right mt-2"
    >
      -
    </div>
  </div>
</div>

      <!-- Vista escritorio -->
      <v-simple-table v-else dense class="battery-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th>Slot</th>
              <th>Estado</th>
              <th>Eficiencia</th>
              <th>Fin</th>
              <th class="text-right">Acción</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="b in getBatteries(selectedSolar)"
              :key="b.id"
              :class="{ 'battery-row-expired': isBatteryExpired(b) }"
            >
              <td>{{ b.slot_num || b.slot || "-" }}</td>

              <td>
                <v-chip
                  x-small
                  :color="isBatteryExpired(b) ? 'error' : 'success'"
                  dark
                  :class="{ 'battery-expired-chip': isBatteryExpired(b) }"
                >
                  {{ isBatteryExpired(b) ? "Caducada" : "Activa" }}
                </v-chip>
              </td>

              <td>{{ batteryEfficiencyText(b) }}</td>

              <td>{{ formatDate(b.fecha_fin || b.end_date) }}</td>

              <td class="text-right">
                <v-btn
                  v-if="canRevampBattery(b)"
                  x-small
                  color="error"
                  outlined
                  :loading="batteryLoading"
                  @click="confirmRevampBattery(b)"
                >
                  Revamping
                </v-btn>

                <span v-else class="text-caption grey--text">
                  -
                </span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <v-divider class="my-3"></v-divider>

      <div class="battery-buy-box">
        <div>
          <div class="font-weight-medium">
            Comprar nueva carta batería
          </div>

          <div class="text-caption grey--text">
            Máximo 2 baterías por planta fotovoltaica.
          </div>

          <div
            v-if="batteryPrice(selectedSolar)"
            class="text-caption mt-1"
          >
            Precio: <b>{{ money(batteryPrice(selectedSolar)) }}</b>
          </div>
        </div>

        <v-btn
        class="battery-buy-btn"
        :disabled="!canBuyBattery(selectedSolar)"
        :loading="batteryLoading"
        @click="confirmBuyBattery"
      >
        Comprar
      </v-btn>
      </div>

      <v-alert
        v-if="batteryCount(selectedSolar) >= 2"
        dense
        outlined
        color="warning"
        class="mt-3"
      >
        Esta planta ya tiene el máximo de 2 baterías.
      </v-alert>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>

      <v-btn class="battery-close-btn" text @click="closeBatteryModal">
        Cerrar
      </v-btn>
    </v-card-actions>
  </v-card>
</v-dialog>
  </div>
</template>

<script>
import { useGameStore } from "@/stores/game";
import { TECH_WEIGHTS, MAX_GRID_CELLS, } from "@/config";
import MyMap from "@/components/MyMap.vue";
import Swal from "sweetalert2";
export default {
  name: "GameCompany",
  components: { MyMap },
  props: {
    generators: { type: Array, required: true },
    totalPoints: { type: Number, required: true },
    budget: { type: Number, required: true },
  },
  data() {
    return { 
      store: useGameStore(),
      MAX_GRID_CELLS,
      capacityWarningShown: false,

      batteryDialog: false,
      selectedSolar: null,
      batteryLoading: false,

      historyDialog: false,
      selectedAssetHistory: null,
      assetHistory: [],
      loadingHistory: false,
      historyLoadingId: null,
      historyClausulazoPrice: null,
    };
  },
  computed: {
    activeLigaId() {
      const rawLigaId =
        this.store.currentAreaId ??
        this.store.player?.activeLigaId ??
        this.store.company?.player?.activeLigaId ??
        this.store.authUser?.active_liga_id ??
        this.store.authUser?.activeLigaId;

      const ligaId = Number(rawLigaId);

      if (!Number.isInteger(ligaId) || ligaId <= 0) {
        return null;
      }

      return ligaId;
    },

    ownedGenerators() {
      return (this.generators || [])
        .filter((g) => (g.owned || 0) > 0)
        .map((g) => ({
          ...g,
          liga_id: g.liga_id ?? this.activeLigaId,
        }));
    },
    ownedGeneratorsCount() {
      return (this.generators || []).length;
    },
    operativeAssetsCount() {
      return (this.generators || []).filter((g) => g.operativo === 1).length;
    },
    totalCapacity() {
      const val = (this.generators || []).reduce(
        (acc, g) => acc + (parseFloat(g.capacity) || 0),
        0
      );
      return Math.round(val * 100) / 100;
    },
    totalOperativeCapacity() {
      const val = (this.generators || []).reduce((acc, g) => {
        if (g.operativo === 1) {
          return acc + (parseFloat(g.capacity) || 0);
        }
        return acc;
      }, 0);
      return Math.round(val * 100) / 100;
    },
    gridSquares() {
      let cells = [];
      const operativeAssets = (this.generators || []).filter((g) => g.operativo === 1);

      for (const g of operativeAssets) {
        const weight = this.getTechWeight(g.type);
        for (let i = 0; i < weight; i++) {
          if (cells.length < MAX_GRID_CELLS) {
            cells.push({ id: `${g.id}-${i}`, type: g.type, x: g.x, y: g.y, name: g.name, provincia: g.provincia });
          }
        }
      }


      while (cells.length < MAX_GRID_CELLS) {
        cells.push(null);
      }

      return cells;
    },
    totalUsedCells() {
      const operativeAssets = (this.generators || []).filter((g) => g.operativo === 1);
      return operativeAssets.reduce((acc, g) => {
        return acc + this.getTechWeight(g.type);
      }, 0);
    },
    capacityPercent() {
  if (!this.MAX_GRID_CELLS) return 0;

  return Math.min(
    100,
    Math.round((this.totalUsedCells / this.MAX_GRID_CELLS) * 100)
  );
}
  },
  watch: {
  activeLigaId(newLigaId, oldLigaId) {
    if (newLigaId === oldLigaId) return;

    this.closeAssetHistory();
    this.closeBatteryModal();

    this.capacityWarningShown = false;

    this.$nextTick(() => {
      this.checkCapacityWarning();
    });
  },

  totalUsedCells: {
    immediate: true,
    handler() {
      this.checkCapacityWarning();
    },
  },
},
  methods: {
    checkCapacityWarning() {
  const usedCells = Number(this.totalUsedCells || 0);

  if (usedCells <= this.MAX_GRID_CELLS) {
    this.capacityWarningShown = false;
    return;
  }

  if (this.capacityWarningShown) {
    return;
  }

  this.capacityWarningShown = true;

  this.showCapacityWarning(usedCells);
},

async showCapacityWarning(usedCells) {
  const exceso = usedCells - this.MAX_GRID_CELLS;

  await Swal.fire({
    icon: "warning",
    title: "Plantilla por encima del límite",
    html: `
      <div style="font-size: 15px; text-align: center;">
        Tu plantilla ocupa actualmente
        <b>${usedCells} de ${this.MAX_GRID_CELLS} casillas</b>.
        <br><br>

        Estás superando el límite permitido en
        <b>${exceso} ${exceso === 1 ? "casilla" : "casillas"}</b>.
        <br><br>

        Desactiva algún activo para volver a estar dentro
        de la capacidad permitida.
        <br><br>

        <b>
          Si mantienes la plantilla por encima del límite,
          podría afectar a la puntuación de tus activos.
        </b>
      </div>
    `,
    confirmButtonText: "Entendido",
    customClass: {
      confirmButton: "clausulazo-swal-confirm",
    },
  });
},
    requireActiveLigaId() {
      const ligaId = Number(this.activeLigaId);

      if (!Number.isInteger(ligaId) || ligaId <= 0) {
        throw new Error(
          "No se ha podido identificar la liga seleccionada. Vuelve a entrar en la liga e inténtalo de nuevo."
        );
      }

      return ligaId;
    },

    emitSellMarket(generator) {
      const ligaId = this.requireActiveLigaId();

      // Se conservan los dos primeros argumentos para no romper el
      // manejador actual del componente padre. El tercero deja también
      // disponible un payload completo para la migración multiliga.
      this.$emit(
        "sell-market",
        generator.id,
        ligaId,
        {
          assetType: "generator",
          id: generator.id,
          liga_id: ligaId,
        }
      );
    },

    getPrecioClausulazo(asset) {
  if (!asset) return 0;

  if (
    asset.precio_clausulazo !== null &&
    asset.precio_clausulazo !== undefined
  ) {
    return Number(asset.precio_clausulazo);
  }

  if (
    asset.precio_compra !== null &&
    asset.precio_compra !== undefined
  ) {
    return Math.round(Number(asset.precio_compra) * 1.1);
  }

  if (
    asset.purchasePrice !== null &&
    asset.purchasePrice !== undefined
  ) {
    return Math.round(Number(asset.purchasePrice) * 1.1);
  }

  return 0;
},
  formatPoints(v) {
  const n = Number(v || 0);

  if (n > 0) {
    return `+${n}`;
  }

  return `${n}`;
},

async openAssetHistory(asset) {
  const assetId =
    asset?.asset_id ||
    asset?.activo_id ||
    asset?.id;

  if (!assetId) {
    return;
  }

  this.selectedAssetHistory = asset;
  this.historyDialog = true;
  this.loadingHistory = true;
  this.historyLoadingId = assetId;

  this.assetHistory = [];
  this.historyClausulazoPrice = null;

  try {
    const ligaId = this.requireActiveLigaId();

    const [history, clausulazo] = await Promise.all([
      this.store.fetchMyAssetHistory(assetId, ligaId),
      this.store.fetchClausulazoPrice(assetId, ligaId),
    ]);

    if (this.historyLoadingId !== assetId) {
      return;
    }

    this.assetHistory = Array.isArray(history)
      ? history
      : [];

    this.historyClausulazoPrice = Number(
      clausulazo?.precio_clausulazo || 0
    );
  } catch (e) {
    const payload = e?.response?.data;

    const message =
      payload?.message ||
      payload?.data?.message ||
      e?.message ||
      "Ha ocurrido un error inesperado.";

    await Swal.fire({
      icon: "error",
      title: "No se pudo cargar el histórico",
      text: message,
      confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
    });

    this.historyDialog = false;
  } finally {
    if (this.historyLoadingId === assetId) {
      this.loadingHistory = false;
      this.historyLoadingId = null;
    }
  }
},

closeAssetHistory() {
  this.historyDialog = false;
  this.selectedAssetHistory = null;
  this.assetHistory = [];
  this.loadingHistory = false;
  this.historyLoadingId = null;
  this.historyClausulazoPrice = null;
},
    hasExpiredBatteries(g) {
  return this.getBatteries(g).some((b) => this.isBatteryExpired(b));
},
    techClass(type) {
  const clean = String(type || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

  return `tech-${clean}`;
},
    canActivate(g) {
      const weight = this.getTechWeight(g.type);
      return this.totalUsedCells + weight <= MAX_GRID_CELLS;
    },
    async toggleOp(g) {
      const newStatus = g.operativo ? 0 : 1;

      if (newStatus === 1 && !this.canActivate(g)) {
        await Swal.fire({
          icon: "warning",
          title: "Capacidad máxima alcanzada",
          text: `No puedes activar este activo porque superarías las ${MAX_GRID_CELLS} celdas disponibles.`,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
        return;
      }

      try {
        const ligaId = this.requireActiveLigaId();

        await this.store.toggleAssetOperativo(
          g.id,
          newStatus,
          ligaId
        );

        this.$emit("refresh", ligaId);
      } catch (e) {
        const payload = e?.response?.data;
        const message =
          payload?.message ||
          payload?.data?.message ||
          e?.message ||
          "No se pudo cambiar el estado operativo del activo.";

        await Swal.fire({
          icon: "error",
          title: "No se pudo actualizar",
          text: message,
          confirmButtonText: "Entendido",
        customClass: {
          confirmButton: "clausulazo-swal-confirm",
        },
        });
      }
    },
    getIcon(type) {
      const t = type.toLowerCase().trim();
      if (t === "eólica") return "mode_fan";
      if (t === "fotovoltaica") return "solar_power";
      if (t === "hidráulica") return "water";
      if (t === "ciclo combinado") return "factory";
      if (t === "nuclear") return "orbit";
      if (t === "carbón") return "precision_manufacturing";
      return "bolt";
    },
    async confirmSellInstant(g) {
  if (g.venta_bloqueada_id) {
    await Swal.fire({
      icon: "info",
      title: "Activo ya en venta",
      text: "Este activo ya está puesto en venta en el mercado. Cancela primero esa venta si quieres venderlo al instante.",
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });
    return;
  }

  const marketPrice = Number(g.marketPrice || 0);

  const estimatedSellPrice = Number(
    g.instantSellPrice ||
    Math.round(marketPrice * 0.70)
  );

  const result = await Swal.fire({
    icon: "warning",
    title: "¿Vender al instante?",
    html: `
      <div style="font-size: 15px;">
        Vas a vender <b>${g.name}</b><br><br>
        Valor actual de mercado:
        <b>${this.money(marketPrice)}</b><br>
        Venta instantánea (70%):
        <b>${this.money(estimatedSellPrice)}</b><br><br>
        Esta acción eliminará el activo de tu plantilla.
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: "Sí, vender",
    cancelButtonText: "Cancelar",
    customClass: {
      confirmButton: "clausulazo-swal-confirm",
    },
  });

  if (!result.isConfirmed) return;

  try {
    const ligaId = this.requireActiveLigaId();

    const data = await this.store.sellOwnedGenerator(
      { ...g, liga_id: ligaId },
      ligaId
    );

    const dineroRecuperado =
      data?.sellPrice ??
      data?.sell_price ??
      estimatedSellPrice;

    await Swal.fire({
      icon: "success",
      title: "Activo vendido",
      html: `
        <div style="font-size: 15px;">
          Has vendido <b>${g.name}</b><br>
          Dinero recuperado: <b>${this.money(dineroRecuperado)}</b>
        </div>
      `,
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });

    this.$emit("refresh", ligaId);
  } catch (e) {
    const payload = e?.response?.data;

    const msg =
      payload?.message ||
      payload?.data?.message ||
      payload?.data ||
      e?.message ||
      "No se pudo vender el activo.";

    await Swal.fire({
      icon: "error",
      title: "No se pudo vender",
      text: msg,
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });
  }
},
isSolar(g) {
  const t = String(g?.type || "").toLowerCase().trim();
  return t === "fotovoltaica" || t === "solar" || t.includes("fotov");
},

getActivoId(g) {
  return g?.id || null;
},

getBatteries(g) {
  return Array.isArray(g?.baterias) ? g.baterias : [];
},

batteryCount(g) {
  return this.getBatteries(g).length;
},

batteryPower(g) {
  const capacity = Number(g?.capacity || 0);
  return Math.round(capacity * 0.5 * 100) / 100;
},

batteryEnergy(g) {
  return Math.round(this.batteryPower(g) * 2 * 100) / 100;
},

batteryPrice(g) {
  return (
    Number(g?.battery_price || 0) ||
    Number(g?.precio_bateria || 0) ||
    Number(g?.precioBattery || 0) ||
    0
  );
},

canBuyBattery(g) {
  return (
    !!this.activeLigaId &&
    this.isSolar(g) &&
    !!this.getActivoId(g) &&
    this.batteryCount(g) < 2
  );
},

openBatteryModal(g) {
  this.selectedSolar = g;
  this.batteryDialog = true;
},

closeBatteryModal() {
  this.batteryDialog = false;
  this.selectedSolar = null;
},

isBatteryExpired(b) {
  const estado = String(b?.estado || "").toLowerCase().trim();

  if (estado === "caducada" || estado === "expirada" || estado === "expired") {
    return true;
  }

  if (b?.puede_revamp === true || b?.puede_revamp === 1) {
    return true;
  }

  return false;
},

canRevampBattery(b) {
  return this.isBatteryExpired(b);
},

batteryEfficiencyText(b) {
  let value =
    b?.eficiencia ??
    b?.efficiency ??
    b?.eficiencia_actual ??
    b?.degradation_factor;

  if (value === undefined || value === null || value === "") {
    return "-";
  }

  value = Number(value);

  if (Number.isNaN(value)) {
    return "-";
  }

  if (value <= 1) {
    value = value * 100;
  }

  return `${Math.round(value)}%`;
},

formatDate(v) {
  if (!v) return "-";

  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;

    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return v;
  }
},
formatFechaProteccion(v) {
  if (!v) return "-";

  try {
    const d = new Date(v);

    if (Number.isNaN(d.getTime())) {
      return v;
    }

    return d.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return v;
  }
},

async confirmBuyBattery() {
  if (!this.selectedSolar) return;

  const precio = this.batteryPrice(this.selectedSolar);

  const result = await Swal.fire({
  icon: "question",
  title: "¿Comprar batería?",
  html: `
    <div style="font-size: 15px;">
      Vas a comprar una batería para <b>${this.selectedSolar.name}</b>.<br><br>
      Potencia: <b>${this.batteryPower(this.selectedSolar)} MW</b><br>
      Duración: <b>2 horas</b><br>
      Energía: <b>${this.batteryEnergy(this.selectedSolar)} MWh</b><br>
      Vida útil: <b>14 días</b><br>
      Degradación: <b>2% diario</b>
      ${precio ? `<br><br>Precio: <b>${this.money(precio)}</b>` : ""}
    </div>
  `,
  showCancelButton: true,
  confirmButtonText: "Sí, comprar",
  cancelButtonText: "Cancelar",
  customClass: {
    confirmButton: "battery-swal-confirm",
  },
});

  if (!result.isConfirmed) return;

  await this.buyBattery();
},

async buyBattery() {
  if (!this.selectedSolar) return;

  this.batteryLoading = true;

  try {
    const activoId = this.getActivoId(this.selectedSolar);

    if (!activoId) {
      throw new Error("No se encontró el identificador del activo.");
    }

    const ligaId = this.requireActiveLigaId();

    await this.store.buyBattery({
      activo_id: activoId,
      liga_id: ligaId,
    });

    await Swal.fire({
      icon: "success",
      title: "Batería comprada",
      text: "La batería se ha añadido correctamente a la planta fotovoltaica.",
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "battery-swal-confirm",
      },
    });

    this.closeBatteryModal();
    this.$emit("refresh", ligaId);
  } catch (e) {
    const payload = e?.response?.data;

    const msg =
      payload?.message ||
      payload?.data?.message ||
      payload?.data ||
      e?.message ||
      "No se pudo comprar la batería.";

    await Swal.fire({
      icon: "error",
      title: "No se pudo comprar",
      text: msg,
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "battery-swal-confirm",
      },
    });
  } finally {
    this.batteryLoading = false;
  }
},

async confirmRevampBattery(battery) {
  const precio =
    Number(battery?.precio_revamp || 0) ||
    Number(battery?.revamp_price || 0) ||
    0;

  const result = await Swal.fire({
    icon: "question",
    title: "¿Hacer revamping?",
    html: `
      <div style="font-size: 15px;">
        La batería volverá a estar activa durante <b>14 días</b>.<br>
        Recuperará el <b>100% de eficiencia</b> el primer día.
        ${precio ? `<br><br>Coste: <b>${this.money(precio)}</b>` : ""}
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: "Sí, renovar",
    cancelButtonText: "Cancelar",
    customClass: {
    confirmButton: "clausulazo-swal-confirm",
  },
  });

  if (!result.isConfirmed) return;

  await this.revampBattery(battery);
},

async revampBattery(battery) {
  this.batteryLoading = true;

  try {
    const ligaId = this.requireActiveLigaId();

    await this.store.revampBattery(
      battery.id,
      ligaId
    );

    await Swal.fire({
      icon: "success",
      title: "Revamping realizado",
      text: "La batería vuelve a estar activa.",
      confirmButtonText: "Entendido",
       customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });

    this.closeBatteryModal();
    this.$emit("refresh", ligaId);
  } catch (e) {
    const payload = e?.response?.data;

    const msg =
      payload?.message ||
      payload?.data?.message ||
      payload?.data ||
      e?.message ||
      "No se pudo hacer el revamping.";

    await Swal.fire({
      icon: "error",
      title: "No se pudo renovar",
      text: msg,
      confirmButtonText: "Entendido",
      customClass: {
        confirmButton: "clausulazo-swal-confirm",
      },
    });
  } finally {
    this.batteryLoading = false;
  }
},
    money(v) {
  const value = Number(v ?? 0);

  if (!Number.isFinite(value)) {
    return "0 €";
  }

  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: "always",
  }).format(value);
},
    normalizarTech(type) {
    return (type || "")
      .toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  },
  getTechWeight(type) {
  const normalizedType = this.normalizarTech(type);

  const entry = Object.entries(TECH_WEIGHTS).find(
    ([key]) => this.normalizarTech(key) === normalizedType
  );

  return entry ? Number(entry[1]) : 1;
},
    getTechCells(type) {
  return this.getTechWeight(type);
},
  },
};
</script>
<style scoped>

.actualizar {
  background-color: #c9dfdd;
  color: white;
}

.orbitron-title {
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 600;
}

/* Mapa de plantilla */
.company-map-wrapper {
  width: 100%;
  max-width: 100%;
  overflow: visible;
  margin-bottom: 16px;
  border-radius: 12px;
}

/* Lista de activos */
.asset-list-item {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  padding-top: 10px;
  padding-bottom: 10px;
}

.asset-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex-shrink: 0;
}

/* Tablet / iPad */
@media (max-width: 960px) {
  .orbitron-title {
    font-size: 18px;
  }

  .company-map-wrapper {
    width: 100%;
    overflow: visible;
  }

  .asset-list-item {
    flex-direction: column;
    align-items: stretch;
  }

  .asset-list-item :deep(.v-list-item__content) {
    width: 100%;
  }

  .asset-actions {
    width: 100%;
    margin-left: 0;
    margin-top: 10px;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .asset-actions .v-btn {
    margin: 0 !important;
  }
}

/* Móvil */
@media (max-width: 600px) {
  .orbitron-title {
    font-size: 16px;
  }

  .actualizar {
    font-size: 10px !important;
    min-width: 74px !important;
    height: 28px !important;
    padding: 0 8px !important;
  }

  .company-map-wrapper {
    width: 100%;
    overflow: visible;
    display: flex;
    justify-content: center;
  }

  .asset-actions {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .asset-actions .v-btn {
    width: 100%;
    font-size: 10px !important;
    min-height: 30px !important;
  }

  .asset-list-item :deep(.v-list-item__title) {
    white-space: normal;
    line-height: 1.2;
  }

  .asset-list-item :deep(.v-list-item__subtitle) {
    white-space: normal;
    line-height: 1.3;
  }
}

/* Móvil muy estrecho */
@media (max-width: 380px) {
  .asset-actions .v-btn {
    font-size: 9px !important;
    min-height: 28px !important;
  }
}
.battery-modal-title {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: 0.4px;
}

.battery-modal-title .material-symbols-outlined {
  color: #31635f;
}

.battery-info-alert {
  background-color: #f8fbfb !important;
  color: #244946 !important;
  border-color: #31635f !important;
}

.battery-summary-box {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  background: #f8fbfb;
  border: 1px solid #dbe9e7;
  border-radius: 12px;
  padding: 12px;
}

.battery-summary-box .text-caption {
  font-family: "Orbitron", sans-serif !important;
  font-size: 10px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.battery-summary-box .font-weight-medium {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-size: 13px;
}
.subtitulo-2{
font-family: "Orbitron", sans-serif !important;
font-size:14px;

}

.battery-buy-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 12px;
  padding: 12px;
}

.battery-buy-box .font-weight-medium {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-size: 13px;
  letter-spacing: 0.3px;
}

.battery-table {
  border: 1px solid #eeeeee;
  border-radius: 10px;
  overflow: hidden;
}

.battery-table th {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f !important;
  font-size: 11px !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.battery-table td {
  font-size: 13px;
}

.battery-buy-btn {
  background: linear-gradient(135deg, #31635f, #4b8f86) !important;
  color: #ffffff !important;
  border-radius: 8px !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 12px !important;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  box-shadow: 0 4px 10px rgba(49, 99, 95, 0.3);
}

.battery-buy-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.battery-close-btn {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f !important;
  font-size: 12px !important;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

@media (max-width: 600px) {
  .battery-summary-box {
    grid-template-columns: 1fr;
  }

  .battery-buy-box {
    flex-direction: column;
    align-items: stretch;
  }

  .battery-buy-box .v-btn {
    width: 100%;
  }

  .battery-modal-title {
    font-size: 15px;
  }
}

.factory-board {
  position: relative;
  background:
    linear-gradient(135deg, #f8fbfb 0%, #eef7f6 45%, #ffffff 100%);
  border: 2px solid #dbe9e7;
  border-radius: 14px;
  padding: 10px 12px;
  box-shadow:
    0 6px 18px rgba(49, 99, 95, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  overflow: hidden;
 max-width: 560px;
  margin: 0 auto 14px auto;

    

}

.factory-board::before {
  content: "";
  position: absolute;
  inset: auto -20px -45px -20px;
  height: 120px;
  background:
    radial-gradient(circle at 20% 50%, rgba(49, 99, 95, 0.12), transparent 32%),
    radial-gradient(circle at 75% 45%, rgba(49, 99, 95, 0.10), transparent 35%);
  pointer-events: none;
}

.factory-roof {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  margin-bottom: 8px;
}

.factory-title {
  display: inline-flex;
  align-items: center;
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.factory-title .material-symbols-outlined {
  font-size: 16px;
}



.factory-chimneys {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 22px;
}

.factory-chimneys span {
  display: block;
  width: 10px;
  border-radius: 3px 3px 0 0;
  background: linear-gradient(180deg, #6b7280, #334155);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.35);
}

.factory-chimneys span:nth-child(1) {
  height: 14px;
}

.factory-chimneys span:nth-child(2) {
  height: 22px;
}

.factory-chimneys span:nth-child(3) {
  height: 17px;
}

.factory-status {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 170px 1fr;
  gap: 10px;
  align-items: center;
  background: rgba(255,255,255,0.78);
  border: 1px solid #e3eeee;
  border-radius: 12px;
  padding: 8px 10px;
  margin-bottom: 10px;
}

.factory-label {
  font-family: "Orbitron", sans-serif !important;
  font-size: 9px;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.4px;
}

.factory-value {
  font-family: "Orbitron", sans-serif !important;
  color: #244946;
  font-size: 11px;
  font-weight: 700;
}

.factory-energy-bar {
  height: 10px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
  border: 1px solid #d1d5db;
}

.factory-energy-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #4b8f86, #7ee787);
  box-shadow: 0 0 10px rgba(126, 231, 135, 0.65);
  transition: width 0.35s ease;
}

.factory-energy-full {
  background: linear-gradient(90deg, #f39c12, #ef4444);
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.55);
}

.factory-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(5, 55px);
  gap: 6px;
  background:
    linear-gradient(180deg, rgba(36,73,70,0.92), rgba(49,99,95,0.86));
  border-radius: 14px;
  padding: 10px;
  border: 2px solid #244946;
  box-shadow:
    inset 0 0 14px rgba(0,0,0,0.20),
    0 6px 14px rgba(36,73,70,0.12);
    width: fit-content;
max-width: 100%;
margin: 0 auto;
}

.factory-cell {
  position: relative;
  aspect-ratio: 1 / 1;
  min-height: 26px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255,255,255,0.85);
  background: rgba(255,255,255,0.18);
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.45),
    0 2px 6px rgba(0,0,0,0.16);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.factory-cell:not(.empty):hover {
  transform: translateY(-3px) scale(1.05);
  z-index: 10;
  box-shadow:
    0 8px 16px rgba(0,0,0,0.25),
    0 0 14px rgba(255,255,255,0.55);
}

.factory-cell.empty {
  border: 1px dashed rgba(255,255,255,0.28);
  background:
    repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.06),
      rgba(255,255,255,0.06) 6px,
      rgba(255,255,255,0.02) 6px,
      rgba(255,255,255,0.02) 12px
    );
  box-shadow: inset 0 0 8px rgba(0,0,0,0.12);
}

.factory-cell-icon {
  font-size: 20px;
  color: #ffffff;
  text-shadow: 0 2px 5px rgba(0,0,0,0.35);
}

.factory-empty-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255,255,255,0.32);
}

.factory-cell-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 118%;
  left: 50%;
  transform: translateX(-50%) translateY(4px);
  min-width: 135px;
  background: #0f172a;
  color: white;
  font-size: 11px;
  line-height: 1.35;
  border-radius: 8px;
  padding: 7px 9px;
  box-shadow: 0 8px 18px rgba(0,0,0,0.35);
  pointer-events: none;
  transition: all 0.16s ease;
  z-index: 30;
  text-align: center;
}

.factory-cell-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #0f172a transparent transparent transparent;
}

.factory-cell:hover .factory-cell-tooltip {
  visibility: visible;
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.factory-floor {
  position: relative;
  z-index: 1;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.factory-floor-line {
  height: 5px;
  flex: 1;
  border-radius: 999px;
  background: linear-gradient(90deg, #31635f, #7caeaa, #31635f);
  opacity: 0.85;
}

.factory-floor-label {
  font-size: 10px;
  color: #60706e;
  white-space: nowrap;
}

/* Colores por tecnología */
.tech-eolica {
  background: linear-gradient(135deg, #248f11, #50b83c);
}

.tech-fotovoltaica {
  background: linear-gradient(135deg, #f6a800, #ffd166);
}

.tech-hidraulica {
  background: linear-gradient(135deg, #257ec7, #4aa3df);
}

.tech-nuclear {
  background: linear-gradient(135deg, #71368a, #a463c7);
}

.tech-ciclo-combinado {
  background: linear-gradient(135deg, #d35400, #e67e22);
}

.tech-carbon {
  background: linear-gradient(135deg, #374151, #111827);
}

.tech-termosolar {
  background: linear-gradient(135deg, #f59e0b, #ef4444);
}

.tech-bombeo-puro {
  background: linear-gradient(135deg, #0ea5e9, #0369a1);
}

@media (max-width: 600px) {
  .factory-board {
    padding: 11px;
    border-radius: 14px;
  }

  .factory-status {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .factory-grid {
    grid-template-columns: repeat(5, minmax(28px, 1fr));
    gap: 6px;
    padding: 10px;
    border-radius: 12px;
  }

  .factory-cell {
    min-height: 32px;
    border-radius: 9px;
  }

  .factory-cell-icon {
    font-size: 19px;
  }

  .factory-floor {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }

  .factory-floor-label {
    white-space: normal;
    text-align: center;
    font-size: 10px;
  }

  .factory-chimneys {
    display: none;
  }

  .factory-title {
    font-size: 12px;
  }

}
.asset-actions .v-btn {
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  border-radius: 7px !important;
}

/* Activar */
.asset-op-btn-on {
  background-color: #2e7d32 !important;
  color: #ffffff !important;
  box-shadow: 0 3px 8px rgba(46, 125, 50, 0.3);
}

/* Desactivar */
.asset-op-btn-off {
  background-color: #f57c00 !important;
  color: #ffffff !important;
  box-shadow: 0 3px 8px rgba(245, 124, 0, 0.3);
}

/* Baterías */
.asset-battery-btn {
  border-color: #31635f !important;
  color: #244946 !important;
  background-color: #f8fbfb !important;
}

/* Poner en venta */
.asset-sell-market-btn {
  border-color: #31635f !important;
  color: #31635f !important;
  background-color: #ffffff !important;
}

/* Vender al instante */
.asset-instant-sell-btn {
  border-color: #d32f2f !important;
  color: #d32f2f !important;
  background-color: #ffffff !important;
}

/* Hover */
.asset-battery-btn:hover,
.asset-sell-market-btn:hover {
  background-color: #e6f1ef !important;
}

.asset-instant-sell-btn:hover {
  background-color: #ffebee !important;
}


.asset-actions :deep(.v-btn.v-btn--disabled) {
  background-color: #eeeeee !important;
  border-color: #9e9e9e !important;
  color: #6b6b6b !important;
  opacity: 1 !important;
  box-shadow: none !important;
}

.asset-actions :deep(.asset-sell-market-btn.v-btn--disabled),
.asset-actions :deep(.asset-instant-sell-btn.v-btn--disabled) {
  background-color: #f4f4f4 !important;
  border-color: #9e9e9e !important;
  color: #777777 !important;
}

.asset-actions :deep(.asset-op-btn.v-btn--disabled) {
  background-color: #bdbdbd !important;
  color: #ffffff !important;
}
.asset-sell-market-btn {
  background: linear-gradient(135deg, #31635f, #4b8f86) !important;
  border: 1px solid #244946 !important;
  color: #ffffff !important;
  box-shadow: 0 3px 8px rgba(49, 99, 95, 0.28);
}

.asset-sell-market-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}
.asset-actions :deep(.asset-sell-market-btn.v-btn--disabled) {
  background: #31635f !important;
  border-color: #244946 !important;
  color: #ffffff !important;
  opacity: 0.85 !important;
  box-shadow: none !important;
}

.asset-actions :deep(.asset-sell-market-btn.v-btn--disabled .v-btn__content) {
  color: #ffffff !important;
  opacity: 1 !important;
}
.generator-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.generator-name {
  min-width: 0;
}

.tech-icon {
  font-size: 20px;
  color: #31635f;
  vertical-align: middle;
}

.tech-cells-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(49, 99, 95, 0.12);
  color: #31635f;
  border: 1px solid rgba(49, 99, 95, 0.25);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.battery-table tr.battery-row-expired {
  background-color: #ffebee !important;
}

.battery-table tr.battery-row-expired td {
  color: #b71c1c !important;
  font-weight: 600;
}

.battery-table tr.battery-row-expired:hover {
  background-color: #ffcdd2 !important;
}

.battery-expired-chip {
  background-color: #d32f2f !important;
  color: #ffffff !important;
  font-weight: 700;
}

.asset-battery-btn-expired {
  background: linear-gradient(135deg, #d32f2f, #ef5350) !important;
  border: 1px solid #b71c1c !important;
  color: #ffffff !important;
  box-shadow: 0 3px 8px rgba(211, 47, 47, 0.28);
}

.asset-battery-btn-expired:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.asset-actions :deep(.asset-battery-btn-expired .v-btn__content) {
  color: #ffffff !important;
}
.history-info-btn {
  width: 27px;
  height: 27px;
  min-width: 27px;
  padding: 0;
  margin: 0 2px;
  border: 1px solid rgba(49, 99, 95, 0.35);
  border-radius: 999px;
  background: rgba(49, 99, 95, 0.1);
  color: #31635f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background-color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.history-info-btn:hover {
  background: #31635f;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(49, 99, 95, 0.28);
}

.history-info-btn:focus {
  outline: 2px solid rgba(49, 99, 95, 0.35);
  outline-offset: 2px;
}

.history-info-icon {
  font-size: 18px !important;
  line-height: 1;
}

.modal-history-title {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-weight: 700;
}

.asset-history-points {
  font-family: "Orbitron", sans-serif !important;
  font-weight: 700;
}

.battery-history-points {
  margin-top: 2px;
  color: #1976d2;
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

@media (max-width: 600px) {
  .history-info-btn {
    width: 25px;
    height: 25px;
    min-width: 25px;
  }

  .history-info-icon {
    font-size: 17px !important;
  }
}
.history-clausulazo-header {
  display: flex;
  align-items: center;
  gap: 6px;
}

.history-clausulazo-price {
  margin-left: auto;
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-size: 18px;
  font-weight: 800;
}
.battery-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.battery-mobile-card {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 12px;
  padding: 12px;
}

.battery-mobile-card-expired {
  background: #fff7f7;
  border-color: #f3d6d6;
}

.battery-mobile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.battery-mobile-slot {
  font-family: "Orbitron", sans-serif !important;
  color: #31635f;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.battery-mobile-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
}

.battery-mobile-item {
  background: #f8fbfb;
  border-radius: 8px;
  padding: 8px;
}

.battery-mobile-label {
  font-size: 10px;
  color: #6b6b6b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 2px;
}

.battery-mobile-value {
  font-size: 13px;
  font-weight: 600;
  color: #244946;
}

@media (max-width: 600px) {
  .battery-mobile-info {
    grid-template-columns: 1fr;
  }

  .battery-buy-box {
    flex-direction: column;
    align-items: stretch;
  }

  .battery-buy-box .v-btn {
    width: 100%;
  }
}

</style>
<style>
.clausulazo-swal-confirm {
  min-height: 36px !important;
  padding: 0 18px !important;
  border: none !important;
  border-radius: 999px !important;

  color: #ffffff !important;
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px !important;
  font-weight: 800 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;

  background: linear-gradient(
    135deg,
    #ff4d6d,
    #ff8a00
  ) !important;

  box-shadow:
    0 0 10px rgba(255, 77, 109, 0.55),
    0 8px 20px rgba(255, 138, 0, 0.25) !important;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease !important;
}

.clausulazo-swal-confirm:hover {
  transform: translateY(-1px) scale(1.03);
  filter: brightness(1.08);

  background: linear-gradient(
    135deg,
    #ff4d6d,
    #ff8a00
  ) !important;

  box-shadow:
    0 0 16px rgba(255, 77, 109, 0.75),
    0 10px 26px rgba(255, 138, 0, 0.38) !important;
}

.clausulazo-swal-confirm:focus {
  background: linear-gradient(
    135deg,
    #ff4d6d,
    #ff8a00
  ) !important;

  box-shadow:
    0 0 0 3px rgba(255, 138, 0, 0.22),
    0 0 14px rgba(255, 77, 109, 0.55) !important;
}
.history-clausulazo-box {
  padding: 14px 16px;
  border: 1px solid rgba(255, 138, 0, 0.32);
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(255, 77, 109, 0.06),
    rgba(255, 138, 0, 0.08)
  );
}

.history-clausulazo-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.history-clausulazo-label {
  display: flex;
  align-items: center;
  gap: 7px;

  color: #d65f00;
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.history-clausulazo-label .material-symbols-outlined {
  font-size: 19px;
}

.history-clausulazo-price {
  margin: 0;
  color: #31635f;
  font-family: "Orbitron", sans-serif !important;
  font-size: 20px;
  font-weight: 800;
  white-space: nowrap;
}

.history-clausulazo-protection {
  display: flex;
  align-items: center;
  gap: 6px;

  margin-top: 10px;
  padding-top: 9px;

  border-top: 1px solid rgba(49, 99, 95, 0.12);

  color: #388e3c;
  font-size: 11px;
}

.history-clausulazo-protection .material-symbols-outlined {
  font-size: 17px;
}

.history-clausulazo-protection strong {
  font-weight: 700;
}

.history-clausulazo-unprotected {
  display: flex;
  align-items: center;
  gap: 6px;

  margin-top: 10px;
  padding-top: 9px;

  border-top: 1px solid rgba(0, 0, 0, 0.08);

  color: #8a8a8a;
  font-size: 11px;
}

.history-clausulazo-unprotected .material-symbols-outlined {
  font-size: 17px;
}

@media (max-width: 600px) {
  .history-clausulazo-main {
    gap: 10px;
  }

  .history-clausulazo-label {
    font-size: 9px;
  }

  .history-clausulazo-price {
    font-size: 17px;
  }

  .history-clausulazo-protection,
  .history-clausulazo-unprotected {
    font-size: 10px;
  }
}

.history-clausulazo-header {
  display: flex;
  align-items: center;
  gap: 6px;

  color: #d65f00;
  font-family: "Orbitron", sans-serif !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

.history-clausulazo-header .material-symbols-outlined {
  font-size: 18px !important;
}

.history-clausulazo-price {
  margin-top: 5px;
  color: #31635f;
  font-family: "Orbitron", sans-serif !important;
  font-size: 19px;
  font-weight: 800;
}

.history-clausulazo-description {
  margin-top: 3px;
  color: #777777;
  font-size: 12px;
  line-height: 1.35;
}
.swal2-confirm.battery-swal-confirm {
  background-color: #31635f !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 4px !important;
  font-family: "Orbitron", sans-serif !important;
  font-weight: 500 !important;
  padding: 10px 22px !important;
}

.swal2-confirm.battery-swal-confirm:hover {
  background-color: #274f4c !important;
  color: #ffffff !important;
}

.swal2-confirm.battery-swal-confirm:focus {
  box-shadow: 0 0 0 3px rgba(49, 99, 95, 0.3) !important;
}
</style>

